/**
 * @file api-wms-dashboard.ts
 * @description Сводка «Склад сегодня» — `GET /admin/wms/dashboard`
 * (phase19.24 этап 1, шаг 3).
 *
 * Одна страница — один запрос. До этого дашборд собирал цифры пятью
 * параллельными выборками (остатки, партии, черновики, списания, движения) и
 * считал их на клиенте: на складском Wi-Fi это пять шансов не загрузиться, а
 * «списаний сегодня» никого не заставляло действовать.
 *
 * Контракт зеркалит сериализатор `apps/api/src/wms/dashboard`. Счётчики —
 * задачи, а не витрина: каждый ведёт в отфильтрованный реестр (маршруты —
 * реестром в `components/wms/dashboard/dashboard-counters.ts`).
 *
 * `warehouseId` в запросе опционален: без него сервер считает по всем складам
 * магазина и возвращает `warehouseId: null`.
 */

import { apiFetch } from "./api-client";
import type { PickingTaskListItemDto } from "./api-picking";
import type { StockMovementDto } from "./api-wms";

/** Шесть чисел шапки дашборда: ключ = ключ записи реестра плиток. */
export interface WmsDashboardCountersDto {
  /** Задания на сборку в работе и ожидании. */
  pickingOpen: number;
  /** Задания, закрытые недостачей и ждущие решения менеджера. */
  pickingShortage: number;
  /** Партии на складе без адреса хранения. */
  unplacedLots: number;
  /**
   * Непроведённые приёмки. Только они: перемещения и списания в черновиках
   * попадают в «Требует внимания» (решение пользователя 2026-09-21).
   */
  draftReceipts: number;
  /** Партии со сроком годности в ближайшие 30 дней. */
  expiringLots: number;
  /** Фасовки, чей остаток опустился ниже минимального. */
  belowMinSkus: number;
  /**
   * phase19.22 (W17-9c) — партии с остатком без складских этикеток. Плитки
   * нет: повод живёт строкой «Требует внимания» (`kind: "unlabeled"`).
   */
  unlabeledLots?: number;
}

/** Повод попасть в «Требует внимания». */
export type WmsAttentionKind =
  | "shortage"
  | "unplaced"
  | "stale_draft"
  | "below_min"
  | "expiring"
  /** phase19.22 (W17-9c) — партии без складских этикеток. */
  | "unlabeled";

/** Куда ведёт строка внимания; маршрут строит реестр на клиенте. */
export type WmsAttentionTargetType =
  | "picking"
  | "receipt"
  | "transfer"
  | "writeoff"
  | "sku"
  | "lot"
  | "bins"
  /** phase19.22 — отбор журнала остатков: `id` — КЛЮЧ отбора (`unlabeled`). */
  | "stock_filter";

/** Адрес объекта строки: вид цели + идентификатор. */
export interface WmsAttentionHrefDto {
  type: WmsAttentionTargetType;
  id: string;
}

/** Строка «Требует внимания» — одна проблема одной фразой. */
export interface WmsAttentionItemDto {
  kind: WmsAttentionKind;
  /** Готовая фраза от сервера: «Z-2026-000012 — недостача 2 позиций». */
  title: string;
  href: WmsAttentionHrefDto;
  /** `danger` — стоит работа, `warning` — успеть до того, как встанет. */
  severity: "warning" | "danger";
  /** Когда это случилось (ISO); сервер может не прислать. */
  at?: string;
}

/** Склад в селекторе шапки — только то, чем он подписан. */
export interface WmsDashboardWarehouseDto {
  id: string;
  name: string;
}

/** Ответ `GET /admin/wms/dashboard`. */
export interface WmsDashboardDto {
  /** Склад отбора; `null` — сводка по всем складам магазина. */
  warehouseId: string | null;
  counters: WmsDashboardCountersDto;
  /** Очередь на сегодня — до пяти заданий с составом (`itemsPreview`). */
  pickingQueue: PickingTaskListItemDto[];
  attention: WmsAttentionItemDto[];
  /** Последние движения — до восьми строк. */
  recentMovements: StockMovementDto[];
  /** Склады магазина: селектор в шапке рисуем только при двух и более. */
  warehouses: WmsDashboardWarehouseDto[];
}

export const wmsDashboardApi = {
  /** Сводка склада; без `warehouseId` — по всем складам магазина. */
  get: (params?: { warehouseId?: string }) => {
    const q = new URLSearchParams();
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    const qs = q.toString();
    return apiFetch<WmsDashboardDto>(`/admin/wms/dashboard${qs ? `?${qs}` : ""}`);
  },
};
